// src/hooks/use-network-status.ts
import { useState, useEffect } from 'react';

export interface NetworkStatus {
  isOnline: boolean;
  connectionType: string;
  effectiveType: string;
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
}

export function useNetworkStatus(): NetworkStatus {
  const [networkStatus, setNetworkStatus] = useState<NetworkStatus>({
    isOnline: true,
    connectionType: 'unknown',
    effectiveType: 'unknown',
    downlink: undefined,
    rtt: undefined,
    saveData: false,
  });

  useEffect(() => {
    // Handle online/offline events
    const handleOnline = () => {
      setNetworkStatus(prev => ({ ...prev, isOnline: true }));
    };

    const handleOffline = () => {
      setNetworkStatus(prev => ({ ...prev, isOnline: false }));
    };

    // Get initial network status
    const updateNetworkStatus = () => {
      const isOnline = navigator.onLine;
      let connectionType = 'unknown';
      let effectiveType = 'unknown';
      let downlink, rtt, saveData;

      // Get connection information if available
      if ('connection' in navigator) {
        const connection = (navigator as any).connection ||
                          (navigator as any).mozConnection ||
                          (navigator as any).webkitConnection;

        if (connection) {
          connectionType = connection.type || 'unknown';
          effectiveType = connection.effectiveType || 'unknown';
          downlink = connection.downlink;
          rtt = connection.rtt;
          saveData = connection.saveData;

          // Listen for connection changes
          const handleConnectionChange = () => {
            setNetworkStatus(prev => ({
              ...prev,
              connectionType: connection.type || 'unknown',
              effectiveType: connection.effectiveType || 'unknown',
              downlink: connection.downlink,
              rtt: connection.rtt,
              saveData: connection.saveData,
            }));
          };

          connection.addEventListener('change', handleConnectionChange);

          return () => {
            connection.removeEventListener('change', handleConnectionChange);
          };
        }
      }

      setNetworkStatus({
        isOnline,
        connectionType,
        effectiveType,
        downlink,
        rtt,
        saveData,
      });
    };

    // Set up event listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initial status update
    const cleanup = updateNetworkStatus();

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (cleanup) cleanup();
    };
  }, []);

  return networkStatus;
}